import { TypeLinkCardSkeleton } from "@contentful/types";
import {
  faArrowRightFromBracket,
  faPhone,
  faUser,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { appendCalendlyUtm } from "@yardzen/js-util";
import { Entry } from "contentful";
import { ComponentProps, forwardRef } from "react";
import { cn } from "../lib/utils";
import { useCurrentHref } from "../lib/use-current-href";
import { Button } from "./button";
import { Link } from "./link";

const IconLink: React.FC<{
  href: string;
  title: string;
  icon: ComponentProps<typeof FontAwesomeIcon>["icon"];
  badge?: number;
  className?: string;
  target?: string;
}> = ({ href, title, icon, badge, className, target }) => (
  <Button
    variant="outline"
    size="icon"
    className="relative !h-7 !w-7 !p-0"
    asChild
  >
    <Link
      href={href}
      title={title}
      className={cn(className)}
      target={target ? target : undefined}
    >
      <FontAwesomeIcon icon={icon} />
      {badge !== undefined && badge > 0 && (
        <span className="bg-brand-primary-light absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-xs text-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  </Button>
);

const SignInIconLink: React.FC<{ className?: string; href?: string }> = ({
  className,
  href,
}) => (
  <IconLink
    href={href || "https://project.yardzen.com/login"}
    title="Log In"
    icon={faUser}
    className={className}
  />
);

const SignOutIconLink: React.FC<{ className?: string }> = ({ className }) => (
  <IconLink
    href="https://project.yardzen.com/api/auth/signout"
    title="Log Out"
    icon={faArrowRightFromBracket}
    className={className}
  />
);

export const UserActions = forwardRef<
  HTMLDivElement,
  {
    loggedIn: boolean;
    className?: string;
    contentfulCtaButton?: Entry<
      TypeLinkCardSkeleton,
      "WITHOUT_UNRESOLVABLE_LINKS",
      string
    >;
    contentfulLoginButton?: Entry<
      TypeLinkCardSkeleton,
      "WITHOUT_UNRESOLVABLE_LINKS",
      string
    >;
    isDesignConsultation?: boolean;
    isDesignClient?: boolean;
    /** Set only for signed-in clients who have an account to go to. */
    myAccountUrl?: string;
  }
>(
  (
    {
      loggedIn,
      className,
      contentfulCtaButton,
      contentfulLoginButton,
      isDesignConsultation,
      isDesignClient,
      myAccountUrl,
    },
    ref,
  ) => {
    const currentHref = useCurrentHref();
    return (
      <div
        ref={ref}
        className={cn("flex shrink items-center justify-end gap-3", className)}
      >
        {!loggedIn && (
          <IconLink
            className="hidden md:inline-flex"
            href={appendCalendlyUtm(
              "https://calendly.com/d/cqks-48t-ckd/project-consultation-with-yardzen",
              "Header Nav",
              currentHref,
            )}
            title="Schedule A Call"
            icon={faPhone}
            target="_blank"
          />
        )}
        {loggedIn && myAccountUrl && (
          <IconLink href={myAccountUrl} title="My Account" icon={faUser} />
        )}
        {loggedIn ? (
          <SignOutIconLink />
        ) : (
          <SignInIconLink
            className="hidden md:inline-flex"
            href={contentfulLoginButton?.fields.url}
          />
        )}

        {!loggedIn && !isDesignConsultation && (
          <Button size="sm" asChild>
            <Link
              className="hidden whitespace-nowrap text-xs font-medium max-[429px]:py-3 max-[390px]:px-3 max-[365px]:whitespace-normal max-[365px]:px-6 max-[365px]:leading-3 min-[360px]:inline-flex"
              href={
                contentfulCtaButton?.fields.url ||
                "https://yardzen.com/design-consultation"
              }
            >
              {contentfulCtaButton?.fields.title || "Start a Project"}
            </Link>
          </Button>
        )}
      </div>
    );
  },
);

UserActions.displayName = "UserActions";

export const UserActionsFloating = forwardRef<
  HTMLDivElement,
  {
    loggedIn: boolean;
    className?: string;
    contentfulCtaButton?: Entry<
      TypeLinkCardSkeleton,
      "WITHOUT_UNRESOLVABLE_LINKS",
      string
    >;
    contentfulLoginButton?: Entry<
      TypeLinkCardSkeleton,
      "WITHOUT_UNRESOLVABLE_LINKS",
      string
    >;
    isDesignConsultation?: boolean;
    isDesignClient?: boolean;
    /** Set only for signed-in clients who have an account to go to. */
    myAccountUrl?: string;
  }
>(
  (
    {
      loggedIn,
      className,
      contentfulCtaButton,
      contentfulLoginButton,
      isDesignConsultation,
      myAccountUrl,
    },
    ref,
  ) => {
    const designQuizUrl = "https://yardzen.com/design-consultation";

    return (
      <div
        ref={ref}
        className={cn("flex shrink items-center justify-end gap-3", className)}
      >
        {loggedIn ? (
          <>
            {myAccountUrl && (
              <Button size="default" asChild>
                <Link href={myAccountUrl}>My Account</Link>
              </Button>
            )}
            <Button size="default" variant="outline" asChild>
              <Link href="/api/auth/signout">Logout</Link>
            </Button>
          </>
        ) : (
          <Button
            size="default"
            variant="secondary"
            className="hidden sm:inline-flex"
            asChild
          >
            <Link href={"/login"}>
              {contentfulLoginButton?.fields.title || "Login"}
            </Link>
          </Button>
        )}

        {!loggedIn && !isDesignConsultation && (
          <Button size="default" variant="zest" asChild>
            <Link
              className="bg-brand-tertiary-50 text-brand-primary-darker hidden whitespace-nowrap text-center font-medium max-[600px]:text-xs max-[450px]:text-xs max-[429px]:py-3 max-[390px]:px-3 max-[365px]:px-6 max-[365px]:leading-3 min-[365px]:inline-flex sm:text-base"
              href={designQuizUrl}
            >
              {contentfulCtaButton?.fields.title || "Start A Consultation"}
            </Link>
          </Button>
        )}
      </div>
    );
  },
);

UserActionsFloating.displayName = "UserActionsFloating";
